import React from 'react';
import styled, {css} from 'styled-components';
import {useTranslations} from "use-intl";

import {Option} from '@tager/web-core';
import {ModalProps} from '@tager/web-components';

import {colors} from '@/constants/theme';
import {ReactComponent as CloseIcon} from '@/assets/svg/Modals/close.svg';
import useModalsOpen from '@/hooks/useModalsOpen';

type Props = ModalProps<{
  value: Option;
  options: Option[];
  onChange: (option: Option) => void;
  alias: string;
  path?: string;
  page?: string;
  filters?: string;
}>;

function MultiSelect({closeModal, innerProps}: Props) {
  const {options, value, onChange} = innerProps;
  const {closeModalState} = useModalsOpen();

  const isSelected = (option: Option) => value.value === option.value;
  const t = useTranslations();

  return (
    <Component>
      <Header>
        <Title>{t('sort.name')}</Title>
        <StyledIcon
          onClick={() => {
            closeModalState();
            closeModal();
          }}
        />
      </Header>
      <Wrapper>
        <Content>
          {options.map((option, index) => (
            <Item
              selected={isSelected(option)}
              key={option.value}
              onClick={() => {
                if (isSelected(option)) {
                  return null;
                }
                onChange(option);
                closeModal();
                closeModalState();
              }}
            >
              {option.label}
            </Item>
          ))}
        </Content>
      </Wrapper>
    </Component>
  );
}

export default MultiSelect;

const Component = styled.div`
  position: relative;
  width: 100vw;
  height: calc(var(--vh, 1) * 100);
  padding-bottom: 20px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  background: ${colors.white};
`;

const Wrapper = styled.div``;
const Content = styled.div`
  padding: 0 15px;
  margin-top: 20px;
  min-width: 320px;

  & > div:not(:first-child) {
    margin-top: 20px;
  }
`;
const Item = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  width: 100%;
  cursor: pointer;
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  border-radius: 5px;
  transition: background 0.25s ease, color 0.25s ease;
  ${({selected}) =>
    selected
      ? css`
        background: ${colors.primary};
        color: ${colors.white};
      `
      : css`
        background: ${colors.gray100};
        color: ${colors.black};
      `};

  &:hover {
    background: ${colors.primary};
    color: ${colors.white};
  }
`;

const Header = styled.div`
  height: 45px;
  position: relative;
  padding: 5px 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 6px 0 #d0d0d0;
`;

const StyledIcon = styled(CloseIcon)`
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  cursor: pointer;
  margin-left: 15px;
`;
const Title = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
`;
